import { Component, Vue } from 'vue-property-decorator';
import { Inject } from 'vue-di-container';
import WithRender from './Login.html';
import moment from 'moment';
import TokenService from '@/Services/TokenService';
import LoginService from './LoginService';
import { Toaster } from '../../Services/toast';
import UserModel from '../../shared/userService/Model/UserModel';
import UserService from '../../shared/userService/UserService';
import UserRoleService from '../../shared/userService/UserRoleService';
import ShortService from '../Short/ShortService';
import { ShortModel } from '../Short/ShortModel';
import jwt_decode from 'jwt-decode';
import { isChangePasswordRequiredFromToken, redirectToSsoLogin } from '@/Services/ssoNavigation';

@WithRender
@Component({
    diProvide: [
        LoginService,
        UserService,
        UserRoleService,
        ShortService,
        TokenService,
    ],
})
export default class Login extends Vue {
    private static readonly SPECIAL_CHAR_REGEX = /[!@#$%]/;
    @Inject(LoginService) public logInService!: LoginService;
    @Inject(TokenService) public tokenService!: TokenService;
    @Inject(UserService) public userService!: UserService;
    @Inject(UserRoleService) public userRoleService!: UserRoleService;
    @Inject(ShortService) public shortService!: ShortService;

    public themeCompany: any = 'Default';
    public logoSrc: any = 'JabberLogo';
    public logoTextSrc: any = 'JabberLogo';
    public userRole: any;
    public userName: string = '';
    public password: string = '';
    public currentPassword: string = '';
    public newPassword: string = '';
    public userNameRe: string = '';
    public confirmPassword: string = '';
    public elementVisible: boolean = true;
    public logInForm: boolean = true;
    public changePasswordForm: boolean = false;
    public isChangePasswordRequired: boolean = false;
    public restoreForm: boolean = false;
    public otpForm: boolean = false;
    public checkRestoreForm: boolean = true;
    public userNameForm: boolean = false;
    public confermForm: boolean = false;
    public email: string = '';
    public data: string = '';
    public otpExpiresAt: string = '';
    public otp: string = '';
    public countDown: number = 0;
    public showPassword: boolean = false;
    public showCurrentPassword: boolean = false;
    public showNewPassword: boolean = false;
    public showConfirmPassword: boolean = false;
    public showPasswordPolicyRules: boolean = false;
    public user!: UserModel;
    updateShortCode: boolean | any = false;
    shortCode: ShortModel[] = [];
    public recapchaResponse: string = '';
    public recapchaResponseFg: string = '';
    public siteKey: string = '';
    private static readonly SENTINEL = 'DEV_BYPASS_TOKEN';
    private static readonly RECAPTCHA_VALIDATION_FAILED = 'Recaptcha validation failed';
    private static readonly TOO_MANY_REQUESTS = 'Too many requests. Please try again later.';
    private recaptchaLoadPromise: Promise<void> | null = null;
    private recaptchaLoaded: boolean = false;

    public async created() {
        const currentPath = window.location.pathname;
        const allowedPaths = ['/AddProviderRequest', '/public-route'];
        const token = localStorage.getItem('token') || sessionStorage.getItem('token');

        if (!token) {
            if (allowedPaths.includes(currentPath)) {
                this.loadCompanyTheme();
                setTimeout(() => (this.elementVisible = false), 2500);
                this.setupRecaptchaForEnvironment();
                return;
            }
            await redirectToSsoLogin();
            return;
        }

        if (!isChangePasswordRequiredFromToken()) {
            const loginPath = currentPath.split('?')[0];
            if (this.$route.name === 'Login' || loginPath.toLowerCase().endsWith('/login')) {
                this.$router.replace({ name: 'Dashboard' });
                return;
            }
            await redirectToSsoLogin();
            return;
        }

        this.loadCompanyTheme();
        setTimeout(() => (this.elementVisible = false), 2500);
        this.setupRecaptchaForEnvironment();
    }

    private loadCompanyTheme() {
        if (
            process.env.VUE_APP_TITLE !== undefined &&
            process.env.VUE_APP_LogoSrc !== undefined &&
            process.env.VUE_APP_LogoTextSrc !== undefined
        ) {
            this.themeCompany = process.env.VUE_APP_TITLE.toString();
            this.logoSrc = process.env.VUE_APP_LogoSrc.toString();
            this.logoTextSrc = process.env.VUE_APP_LogoTextSrc.toString();
        } else {
            this.themeCompany = 'Default';
            this.logoSrc = 'JabberLogo';
            this.logoTextSrc = 'JabberLogo';
        }
    }

    private setupRecaptchaForEnvironment() {
        const hostname = window.location.hostname;
        const isLocal = hostname === 'localhost' || hostname === '127.0.0.1';
        if (isLocal) {
            this.recapchaResponse = Login.SENTINEL;
            this.recapchaResponseFg = Login.SENTINEL;
            this.siteKey = '';
        } else {
            this.siteKey = (this.logInService.GetRecaptchaSiteKey() || '').toString().trim();
            if (this.siteKey) {
                this.recaptchaLoadPromise = this.loadRecaptchaV3(this.siteKey);
            }
        }
    }

    public async mounted() {
        // Do not reopen "Change password" from a stale JWT on /Login refresh — that blocked signing in as
        // another user. Change-password UI is shown from login() / verfiyOTP() after a successful auth.
        const token = await this.tokenService.getToken();
        if (!token) {
            return;
        }
        try {
            jwt_decode(token);
        } catch {
            localStorage.removeItem('token');
        }
    }

    public showRestoreForm() {
        this.logInForm = false;
        this.restoreForm = true;
    }

    public showEmailForm() {
        this.checkRestoreForm = false;
        this.userNameForm = true;
    }

    public async resetPassword() {
        if (!this.isValidResetPassword) return;
        try {
            this.recapchaResponseFg = await this.getRecaptchaToken('resetPassword');
        } catch {
            Toaster.error(Login.RECAPTCHA_VALIDATION_FAILED);
            return;
        }

        const response = await this.logInService.ResendPasswordEmail(this.userNameRe, this.recapchaResponseFg);
        if (response.isSuccess) {
            this.userNameForm = false;
            this.confermForm = true;
            this.userNameRe = '';
        } else {
            this.toastAuthApiError(response.errorMessage, false);
        }
    }

    public backLogin() {
        this.confermForm = false;
        this.checkRestoreForm = true;
        this.restoreForm = false;
        this.logInForm = true;
    }

    public switchToOtpForm() {
        this.confermForm = false;
        this.checkRestoreForm = false;
        this.restoreForm = false;
        this.logInForm = false;
        this.otpForm = true;
    }
    public get currentYear() {
        return moment().year();
    }

    private async getRecaptchaToken(action: string): Promise<string> {
        if (this.recapchaResponse === Login.SENTINEL) {
            return Login.SENTINEL;
        }

        if (!this.siteKey || this.siteKey.trim() === '') {
            this.siteKey = (this.logInService.GetRecaptchaSiteKey() || '').toString().trim();
        }

        if (!this.siteKey) {
            throw new Error('reCAPTCHA site key is empty');
        }

        await (this.recaptchaLoadPromise || this.loadRecaptchaV3(this.siteKey));

        return new Promise((resolve, reject) => {
            const g: any = (window as any).grecaptcha;
            if (!g || !g.ready || !g.execute) {
                reject(new Error('grecaptcha not available after script load'));
                return;
            }
            g.ready(() => {
                g.execute(this.siteKey, { action })
                    .then((token: string) => resolve(token))
                    .catch((e: any) => reject(e));
            });
        });
    }

    public async login() {
        if (!this.isValid) return;
        try {
            this.recapchaResponse = await this.getRecaptchaToken('login');
        } catch {
            Toaster.error(Login.RECAPTCHA_VALIDATION_FAILED);
            return;
        }

        const result = await this.logInService.login(
            this.userName,
            this.password,
            this.recapchaResponse
        );

        if (result.isSuccess && result.isOTPEnabled) {
            this.data = result.data;
            this.otpExpiresAt = result.expiresAt;
            this.countDown = result.duration;
            this.switchToOtpForm();
            this.countDownTimer();
        } else if (result.isSuccess && !result.isOTPEnabled) {
            await this.tokenService.setToken(result.data);
            const expiresIn = parseInt(result.duration, 0);
            localStorage.timer = expiresIn * 60;
            localStorage.expiresIn = moment().add(expiresIn, 'minutes').format('lll');
            await this.chickChangePasswordRequired();
            if (this.isChangePasswordRequired) {
                this.logInForm = false;
                this.changePasswordForm = true;
            } else {
                this.redirectToMainPage();
            }
        } else {
            this.recapchaResponse = '';
            this.toastAuthApiError(result.errorMessage, true);
        }
    }

    public get isValid(): boolean {
        return this.userName !== '' && this.password !== '' ;
    }

    public get isValidResetPassword(): boolean {
        return this.userNameRe !== '';
    }

    /** Save enabled when fields filled, policy met, and new matches confirm (button disabled if mismatch). */
    public get isValidChangePassword(): boolean {
        return (this.currentPassword !== '' &&
            this.newPassword !== '' &&
            this.confirmPassword !== '' &&
            this.isStrongPassword
        );
    }

    /** Explicit binding for disabled state (click + Enter both respect validity). */
    public get changePasswordSubmitDisabled(): boolean {
        return !this.isValidChangePassword;
    }

    public submitChangePasswordIfValid(): void {
        if (!this.isValidChangePassword) {
            return;
        }
        this.changePassword();
    }

    public get hasMinLength(): boolean {
        return this.newPassword.length >= 8;
    }

    public get hasMaxLength(): boolean {
        return this.newPassword.length <= 64;
    }

    public get hasUppercaseLetter(): boolean {
        return /[A-Z]/.test(this.newPassword);
    }

    public get hasLowercaseLetter(): boolean {
        return /[a-z]/.test(this.newPassword);
    }

    public get hasNumber(): boolean {
        return /[0-9]/.test(this.newPassword);
    }

    public get hasSpecialCharacter(): boolean {
        return Login.SPECIAL_CHAR_REGEX.test(this.newPassword);
    }

    public get doesNotContainUsername(): boolean {
        const normalizedUsername = (this.userName || this.userNameRe || '').trim().toLowerCase();
        if (!normalizedUsername) {
            return true;
        }
        return !this.newPassword.toLowerCase().includes(normalizedUsername);
    }

    public get isNotSimpleSequence(): boolean {
        // Align with backend: ascending-sequence window is 6 characters (UserService.ContainsSimpleSequence).
        if (!this.newPassword || this.newPassword.length < 6) {
            return false;
        }
        return !this.containsSimpleSequence(this.newPassword);
    }

    public get isNotSameAsCurrentPassword(): boolean {
        if (!this.currentPassword || !this.newPassword) {
            return true;
        }
        return this.currentPassword !== this.newPassword;
    }

    public get isStrongPassword(): boolean {
        return this.hasMinLength &&
            this.hasMaxLength &&
            this.hasUppercaseLetter &&
            this.hasLowercaseLetter &&
            this.hasNumber &&
            this.hasSpecialCharacter &&
            this.isNotSimpleSequence;
    }

    public get passwordPolicyRules(): Array<{ label: string; isMet: boolean }> {
        return [
            { label: 'at least 8 characters', isMet: this.hasMinLength },
            { label: 'an uppercase letter (A-Z)', isMet: this.hasUppercaseLetter },
            { label: 'a lowercase letter (a-z)', isMet: this.hasLowercaseLetter },
            { label: 'a number (0-9)', isMet: this.hasNumber },
            { label: 'a special character (! @ # $ %)', isMet: this.hasSpecialCharacter },
            { label: 'no simple ascending sequence (e.g. 123456 / abcdef)', isMet: this.isNotSimpleSequence },
        ];
    }

    public GetUserRole() {
        return this.userRoleService.GetUserRole(this.user);
    }

    public async changePassword() {
        if (this.newPassword !== this.confirmPassword) {
            Toaster.error('The new password and confirmation do not match');
            return;
        }
        if (!this.isValidChangePassword) return;

        // Hidden rules: show them via toast instead of rendering inside the UI list.
        if (!this.doesNotContainUsername) {
            Toaster.error('Password must not contain username');
            return;
        }

        if (!this.isNotSameAsCurrentPassword) {
            Toaster.error('Password was used before');
            return;
        }

        const result = await this.logInService.changePassword(
            this.user.userId, this.currentPassword,
            this.newPassword, this.confirmPassword);
        if (!result || !result.isSuccess) {
            Toaster.error((result && result.errorMessage) ? result.errorMessage : 'Change password failed');
            return;
        }
        const payload = result.result;
        const token = payload && (payload.token as string | undefined);
        if (!token) {
            Toaster.error('Password changed but session could not be refreshed. Please sign in again.');
            return;
        }
        await this.tokenService.setToken(token);
        const expiresIn = typeof payload.expiresIn === 'number' ? payload.expiresIn : 120;
        localStorage.timer = expiresIn * 60;
        localStorage.expiresIn = moment().add(expiresIn, 'minutes').format('lll');
        this.changePasswordForm = false;
        this.currentPassword = '';
        this.newPassword = '';
        this.confirmPassword = '';
        Toaster.success('Password changed successfully');
        this.redirectToMainPage();
    }

    public async chickChangePasswordRequired() {
        this.user = await this.userService.getUser();
        this.isChangePasswordRequired =
            this.user.details.ChangePasswordRequired.toLowerCase() === 'true';
    }


    public async verfiyOTP() {
        if (!this.otp) return;
        const result = await this.logInService.verfiyOTP(this.data, this.otpExpiresAt, this.otp);
        if (result.isSuccess) {
            await this.tokenService.setToken(result.token);
            const expiresIn = parseInt(result.expiresIn, 0);
            localStorage.timer = expiresIn * 60;
            localStorage.expiresIn = moment().add(expiresIn, 'minutes').format('lll');
            await this.chickChangePasswordRequired();
            if (this.isChangePasswordRequired) {
                this.otpForm = false;
                this.changePasswordForm = true;
            } else {
                if (this.user.details.Role === 'Provider Admin' || this.user.details.Role === 'Provider User') {
                    this.validateOnShortCodeTypes()
                }
                else {
                    this.redirectToMainPage();
                }
            }
        } else {
            this.toastAuthApiError(result.errorMessage, true);
            if (result.isLocked) {
                this.otpForm = false;
                this.logInForm = true;
            }
        }
    }
    public countDownTimer() {
        if (this.countDown > 0) {
            setTimeout(() => {
                this.countDown -= 1;
                this.countDownTimer();
            }, 1000);
        }
    }
    public get isResendValid(): boolean {
        return (this.countDown > 0);
    }
    public async resendOtp() {
        const result = await this.logInService.resendOTP(this.data);
        if (result.isSuccess) {
            this.data = result.data;
            this.otpExpiresAt = result.expiresAt;
            this.countDown = result.duration;
            this.countDownTimer();
        } else {
            this.toastAuthApiError(result.errorMessage, true);
        }
    }

    public changeLanguage() {
        if (localStorage.dir === 'ltr') {
            localStorage.dir = 'rtl';
        } else {
            localStorage.dir = 'ltr';
        }
        this.$router.go(0);
    }
    redirectToMainPage() {
        if (this.GetUserRole() == 16) {
            this.$router.push({ name: 'SenderComplaints' });
        } else {
            this.$router.push({ name: 'Dashboard' });
        }
    }

    showUpdateShortCodeDialog() {
        this.updateShortCode = true;
    }

    hideUpdateShortCodeDialog() {
        this.updateShortCode = false;
        this.redirectToMainPage();
    }

    async validateOnShortCodeTypes() {
        var responseShortCode = await this.shortService.getAll();
        this.shortCode = responseShortCode.result;
        if (this.shortCode.filter(s => s.shortCodeType === null).length > 0) {
            this.showUpdateShortCodeDialog();
            this.otpForm = false;
        }
        else {
            this.redirectToMainPage();
        }
    }

    private toastAuthApiError(errorMessage: string, translate: boolean): void {
        if (!errorMessage) {
            return;
        }
        if (errorMessage === Login.RECAPTCHA_VALIDATION_FAILED ||
            errorMessage === Login.TOO_MANY_REQUESTS) {
            Toaster.error(errorMessage);
            return;
        }
        if (translate) {
            Toaster.error(this.$t(errorMessage).toString());
            return;
        }
        Toaster.error(errorMessage);
    }

    private loadRecaptchaV3(siteKey: string): Promise<void> {
        if (this.recaptchaLoaded) {
            return Promise.resolve();
        }

        if (this.recaptchaLoadPromise) {
            return this.recaptchaLoadPromise;
        }

        this.recaptchaLoadPromise = new Promise((resolve, reject) => {
            const w: any = window as any;
            if (w.grecaptcha && w.grecaptcha.execute) {
                this.recaptchaLoaded = true;
                resolve();
                return;
            }

            const existing = document.getElementById('recaptcha-v3') as HTMLScriptElement | null;
            if (existing) {
                existing.onload = () => {
                    this.recaptchaLoaded = true;
                    resolve();
                };
                existing.onerror = () => reject(new Error('Failed to load reCAPTCHA script'));
                return;
            }

            const s = document.createElement('script');
            s.id = 'recaptcha-v3';
            s.src = 'https://www.google.com/recaptcha/api.js?render=' + encodeURIComponent(siteKey);
            s.async = true;
            s.defer = true;

            s.onload = () => {
                this.recaptchaLoaded = true;
                resolve();
            };
            s.onerror = () => reject(new Error('Failed to load reCAPTCHA script'));

            document.head.appendChild(s);
        });

        return this.recaptchaLoadPromise;
    }

    private containsSimpleSequence(password: string): boolean {
        if (!password || password.length < 6) {
            return false;
        }

        const lowerPassword = password.toLowerCase();
        const sequenceLength = 6;

        for (let index = 0; index <= lowerPassword.length - sequenceLength; index += 1) {
            const chunk = lowerPassword.substring(index, index + sequenceLength);
            if (this.isAscendingSequence(chunk, '0123456789') || this.isAscendingSequence(chunk, 'abcdefghijklmnopqrstuvwxyz')) {
                return true;
            }
        }

        return false;
    }

    private isAscendingSequence(value: string, source: string): boolean {
        for (let index = 0; index <= source.length - value.length; index += 1) {
            if (source.substring(index, index + value.length) === value) {
                return true;
            }
        }
        return false;
    }

}